// apps/web/src/components/chat/forms/TrialResultsForm.tsx
import {
  Button,
  Typography,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { formatMetricValue } from '@diggens/shared-utils';

interface ExtractedMetric {
  name: string;
  value: any;
  confidence: number;
  source_location?: string;
}

interface TrialResultsData {
  submission_id: string;
  company_name: string;
  period: string;
  metrics_extracted: number;
  metrics: ExtractedMetric[];
}

interface TrialResultsFormProps {
  data: TrialResultsData;
  tenantId: string;
  onSuccess: (result: { action: string }) => void;
  onClose: () => void;
}


function getConfidenceColor(confidence: number): 'success' | 'warning' | 'error' {
  if (confidence >= 0.8) return 'success';
  if (confidence >= 0.5) return 'warning';
  return 'error';
}

export function TrialResultsForm({ data, onSuccess, onClose }: TrialResultsFormProps) {
  const handleSetupFund = () => {
    onSuccess({ action: 'setup_fund' });
  };

  const highConfidenceCount = data.metrics.filter((m) => m.confidence >= 0.8).length;
  const lowConfidenceCount = data.metrics.filter((m) => m.confidence < 0.8).length;

  return (
    <Box sx={{ p: 3, maxWidth: 700 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CheckCircleIcon color="success" />
        <Typography variant="h6">Extraction Complete</Typography>
      </Box>

      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Company
            </Typography>
            <Typography variant="body1">{data.company_name}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Period
            </Typography>
            <Typography variant="body1">{data.period}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Metrics Found
            </Typography>
            <Typography variant="body1">{data.metrics_extracted}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${highConfidenceCount} high confidence`}
            size="small"
            color="success"
            variant="outlined"
          />
          {lowConfidenceCount > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${lowConfidenceCount} needs review`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
        </Box>
      </Paper>

      {/* Metrics Table */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Extracted Metrics
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 300 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell align="center">Confidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.metrics.map((metric, index) => (
              <TableRow key={index}>
                <TableCell>{metric.name}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                  {formatMetricValue(metric.value)}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={`${Math.round(metric.confidence * 100)}%`}
                    size="small"
                    color={getConfidenceColor(metric.confidence)}
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleSetupFund}>
          Set Up My Fund
        </Button>
      </Box>
    </Box>
  );
}
